import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import useSaveFcmToken from "../hooks/useSaveFcmToken";
import { db } from "../firebase/config";
import { addDoc, collection, doc, getDoc, getDocs, serverTimestamp } from "firebase/firestore";
import MarqueeBanner from "../components/MarqueeBanner";
import Modal from "../components/Modal";
import PlanCard from "../components/PlanCard";
import LocationPicker from "../components/LocationPicker";

const UPCOMING_PRODUCTS = [
  {
    name: "Cut Fruits Subscription",
    description: "Plans start @1299",
    imageUrl: "/images/upcoming-fruits.jpg"
  },
  {
    name: "Bakshalu/Babbatlu",
    description: "Sweet puri",
    imageUrl: "/images/upcoming-bakshalu.jpg"
  }
];

const Home = ({ onSelectPlan }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Auto-save FCM token for authenticated users
  useSaveFcmToken();
  
  // State for plans fetched from Firestore
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  
  // Custom order state
  const [showCustomOrder, setShowCustomOrder] = useState(false);
  const [qty, setQty] = useState(2);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [customOrderLocation, setCustomOrderLocation] = useState({ address: "", lat: "", lng: "" });
  const [customOrderName, setCustomOrderName] = useState("");
  const [customOrderMobile, setCustomOrderMobile] = useState("");

  const [showInterestModal, setShowInterestModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestMessage, setInterestMessage] = useState("");

  // Add delivery charge state
  const [deliveryCharge, setDeliveryCharge] = useState(0);

  // Fetch plans from Firestore
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const plansRef = collection(db, "subscription_plans");
        const snapshot = await getDocs(plansRef);
        
        const fetchedPlans = [];
        snapshot.forEach((doc) => {
          const planData = doc.data();
          fetchedPlans.push({
            id: doc.id,
            name: planData.name,
            price: planData.price,
            rotisPerDay: planData.rotisPerDay,
            details: planData.details || [],
            note: planData.note || "",
            isActive: planData.isActive !== false // Default to true if not specified
          });
        });
        
        // Sort plans by price (ascending)
        fetchedPlans.sort((a, b) => a.price - b.price);
        
        setPlans(fetchedPlans);
      } catch (error) {
        console.error("Error fetching plans:", error);
        // Fallback to default plans if Firestore fetch fails
        setPlans([
          {
            name: "Basic Plan",
            price: 1299,
            rotisPerDay: 2,
            details: ["🥟 2 rotis/day", "📅 Monthly", "🚚 Free Delivery", "👤 For light eaters"],
            note: "Ideal for light eaters"
          },
          {
            name: "Standard Plan",
            price: 2999,
            rotisPerDay: 5,
            details: ["🥟 5 rotis/day", "📅 Monthly", "🚚 Free Delivery", "👨‍👩‍👧‍👦 For 2–3 people"],
            note: "Serves 2–3 members"
          },
          {
            name: "Mega Plan",
            price: 5999,
            rotisPerDay: 10,
            details: ["🥟 10 rotis/day", "🎁 Sat: Special Sweet", "🚚 Free Delivery", "👨‍👩‍👧‍👦 4–5 members"],
            note: "Serves 4–5 members"
          }
        ]);
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    setDeliveryCharge(30);
  }, []);

  const handleSubscribe = (plan) => {
    if (!user) {
      alert("Please log in to place your subscription order");
      navigate("/login");
      return;
    }
    
    console.log("🔍 Plan being subscribed to:", plan); // Debug log
    
    const planDetails = {
      id: plan.id, // Include the Firestore document ID
      name: plan.name,
      price: plan.price,
      description: (plan.details || []).join(" • "),
      note: plan.note,
      rotisPerDay: plan.rotisPerDay || parseInt((plan.details && plan.details[0]?.match(/\d+/)?.[0]) || "2")
    };
    
    console.log("🔍 Plan details being passed:", planDetails); // Debug log
    
    onSelectPlan(planDetails);
    navigate("/subscribe");
  };

  const handleCustomOrderLocationSelect = (loc) => {
    setCustomOrderLocation(loc);
  };

  const placeCustomOrder = async () => {
    if (!customOrderName || !customOrderMobile || !customOrderLocation.address || !deliveryDate) {
      alert("Please fill all fields, including Name, Mobile, and Location");
      return;
    }
    if (!user) {
      alert("Please log in to place a custom order");
      navigate("/login");
      return;
    }
    const total = (qty * 20) + deliveryCharge;
    try {
      await addDoc(collection(db, "custom_orders"), {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || customOrderName,
        phone: customOrderMobile,
        qty,
        deliveryDate,
        area: customOrderLocation.area || "",
        addressDetails: customOrderLocation.address || "",
        lat: customOrderLocation.lat,
        lng: customOrderLocation.lng,
        deliveryCharge,
        total,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      alert("Order placed!");
      setShowCustomOrder(false);
      setQty(2);
      setDeliveryDate("");
      setCustomOrderLocation({ address: "", lat: "", lng: "" });
      setCustomOrderName("");
      setCustomOrderMobile("");
    } catch (err) {
      console.error(err);
      alert("Failed to place order");
    }
  };

  const handleInterestSubmit = async () => {
    if (!user) {
      setInterestMessage("Please log in to express interest.");
      setTimeout(() => {
        setShowInterestModal(false);
        navigate("/login");
      }, 2000);
      return;
    }
    if (selectedProducts.length === 0) {
      setInterestMessage("Please select at least one product.");
      return;
    }
    setInterestSubmitting(true);
    try {
      await addDoc(collection(db, "product_interest"), {
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || customOrderName || "",
        products: selectedProducts,
        createdAt: serverTimestamp(),
      });
      setInterestMessage("Thank you for your interest!");
      setSelectedProducts([]);
      setTimeout(() => setShowInterestModal(false), 1500);
    } catch (err) {
      setInterestMessage("Failed to submit interest. Try again.");
    } finally {
      setInterestSubmitting(false);
    }
  };

  return (
    <div className="font-sans">
      <Header user={user} />
      
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-green-100 via-green-50 to-white px-6 py-16 text-center">
        <h1 className="text-3xl md:text-5xl font-bold text-green-800 mb-4">
          Freshly Made at Home, Delivered with Love ❤️
        </h1>
        <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
          Delicious home-style rotis, served daily to your doorstep. Made fresh every morning with love and care.
        </p>
        <button
          onClick={() => document.getElementById('plans-section').scrollIntoView({ behavior: 'smooth' })}
          className="bg-green-600 text-white text-lg px-8 py-4 rounded-full shadow-lg hover:bg-green-700 transition-all duration-200 transform hover:scale-105"
        >
          🚀 Explore Plans
        </button>
      </div>

      <MarqueeBanner />

      {/* Plans Section */}
      <div id="plans-section" className="px-4 py-16 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">Choose Your Perfect Plan</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Select the plan that fits your family's needs. All plans include free delivery and fresh, homemade rotis.
          </p>
        </div>
        
        <div className="grid md:grid-cols-4 gap-8">
          {loadingPlans ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-xl p-8 border border-green-100 animate-pulse">
                <div className="text-center mb-6">
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-12 bg-gray-200 rounded mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
                <div className="space-y-3 mb-6">
                  {Array.from({ length: 4 }).map((_, j) => (
                    <div key={j} className="h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : (
            <>
              {plans.map((plan, i) => (
                <PlanCard
                  key={plan.id || i}
                  name={plan.name}
                  price={`₹${plan.price}`}
                  description={plan.details ? plan.details.join(" • ") : ""}
                  note={plan.note}
                  rotisPerDay={plan.rotisPerDay}
                  onSubscribe={handleSubscribe}
                  imageUrl={
                    i === 0 ? "/images/basic-plan.jpg" :
                    i === 1 ? "/images/standard-plan.jpg" :
                    i === 2 ? "/images/mega-plan.jpg" :
                    "/images/plan-placeholder.png"
                  }
                />
              ))}
              {/* Upcoming Products Card */}
                    {UPCOMING_PRODUCTS.map((prod, idx) => (
                <div key={prod.name} className="bg-white rounded-3xl shadow-xl p-0 mb-8 max-w-xs mx-auto flex flex-col border border-gray-100 hover:shadow-2xl transition-all duration-300">
                  <div className="h-2 w-full rounded-t-3xl bg-gradient-to-r from-green-400 to-green-600"></div>
                  <div className="w-full h-40 flex items-center justify-center bg-gray-50 rounded-t-3xl overflow-hidden">
                    <img
                      src={prod.imageUrl || "/images/upcoming-placeholder.png"}
                      alt={prod.name}
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between p-6">
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold text-green-800 mb-1">{prod.name}</h3>
                      <div className="text-gray-700 mb-2">{prod.description}</div>
                </div>
                <button
                      className="w-full bg-green-600 text-white py-3 rounded-full font-semibold text-lg hover:bg-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg mt-2"
                  onClick={() => { setShowInterestModal(true); setInterestMessage(""); }}
                >
                  I'm Interested
                </button>
              </div>
                </div>
              ))}
          {/* Custom Order Card */}
              <div className="bg-white rounded-3xl shadow-xl p-0 mb-8 max-w-xs mx-auto flex flex-col border border-gray-100 hover:shadow-2xl transition-all duration-300">
                <div className="h-2 w-full rounded-t-3xl bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
                <div className="w-full h-40 flex items-center justify-center bg-gray-50 rounded-t-3xl overflow-hidden">
                  <img
                    src="/images/custom-order.jpg"
                    alt="Custom Order"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="flex-1 flex flex-col justify-between p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-yellow-800 mb-1">Custom Order</h3>
                    <div className="text-2xl font-extrabold text-yellow-700 mb-1">₹20 /- Per Roti</div>
                    <div className="text-gray-700 mb-2">Choose quantity, location and we'll deliver.</div>
                    <div className="text-sm text-red-600 font-medium">Delivery Charges Apply</div>
            </div>
            <button
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-full font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-lg mt-2"
              onClick={() => setShowCustomOrder(true)}
            >
              Place Custom Order
            </button>
          </div>
              </div>
            </>
          )}
        </div>
        {/* Custom Order Modal/Form */}
        {showCustomOrder && (
          <div className="mt-8 border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">🛒 Custom Order Form</h3>
            <div className="space-y-4">
              <div>
                <label>Name:</label>
                <input
                  type="text"
                  className="w-full border rounded p-2"
                  value={customOrderName}
                  onChange={(e) => setCustomOrderName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Mobile:</label>
                <input
                  type="tel"
                  className="w-full border rounded p-2"
                  value={customOrderMobile}
                  onChange={(e) => setCustomOrderMobile(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Quantity (Rotis):</label>
                <div className="flex items-center gap-3 mt-1">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-1 bg-gray-200 rounded">-</button>
                  <span>{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-3 py-1 bg-gray-200 rounded">+</button>
                </div>
              </div>
              <div>
                <label>Delivery Date:</label>
                <input
                  type="date"
                  className="w-full border rounded p-2"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Address:</label>
                <LocationPicker onLocationSelect={handleCustomOrderLocationSelect} />
                {customOrderLocation.lat && (
                  <div className="text-xs text-gray-500 mt-1">
                    ✅ Selected: {customOrderLocation.address}<br />
                    Coordinates: {customOrderLocation.lat}, {customOrderLocation.lng}
                  </div>
                )}
              </div>
              <div>
                <strong>Total Price: ₹{(qty * 20) + deliveryCharge}</strong>
                <p className="text-xs text-gray-600">₹{deliveryCharge} delivery charge included</p>
              </div>
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded"
                onClick={placeCustomOrder}
              >
                ✅ Confirm Custom Order
              </button>
              <button
                className="ml-4 text-gray-500 underline text-sm"
                onClick={() => setShowCustomOrder(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Interest Modal */}
        {showInterestModal && (
          <Modal onClose={() => setShowInterestModal(false)}>
            <h2 className="text-lg font-bold mb-4 text-green-700">Select Products You're Interested In</h2>
            <form onSubmit={e => { e.preventDefault(); handleInterestSubmit(); }}>
              {UPCOMING_PRODUCTS.map((prod, idx) => (
                <label key={idx} className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selectedProducts.includes(prod.name)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedProducts([...selectedProducts, prod.name]);
                      } else {
                        setSelectedProducts(selectedProducts.filter(p => p !== prod.name));
                      }
                    }}
                  />
                  {prod.name}
                </label>
              ))}
              {interestMessage && <div className="text-red-600 text-sm mb-2">{interestMessage}</div>}
              <button
                type="submit"
                className="bg-green-600 text-white px-4 py-2 rounded-full shadow hover:bg-green-700 transition-all mt-2"
                disabled={interestSubmitting}
              >
                {interestSubmitting ? "Submitting..." : "Submit Interest"}
              </button>
            </form>
          </Modal>
        )}
      </div>

      {/* Trust Section */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold text-green-700 mb-4">Why Choose JR Home Made Foods?</h3>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            1000+ customers trust us for daily meals. Made fresh, packed with care, and delivered to your door.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">🏠</div>
              <h4 className="font-bold text-green-700 text-lg mb-2">Home Made</h4>
              <p className="text-gray-600">Freshly prepared in our kitchen with love and care</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">🚚</div>
              <h4 className="font-bold text-green-700 text-lg mb-2">Free Delivery</h4>
              <p className="text-gray-600">Delivered fresh to your doorstep daily</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="text-4xl mb-3">❤️</div>
              <h4 className="font-bold text-green-700 text-lg mb-2">Made with Love</h4>
              <p className="text-gray-600">Quality ingredients & care in every bite</p>
            </div>
          </div>
          
          <div className="flex justify-center gap-8 flex-wrap text-gray-700 font-medium">
            <span className="flex items-center">✅ Fresh Ingredients</span>
            <span className="flex items-center">🚚 On-Time Delivery</span>
            <span className="flex items-center">📞 24x7 Support</span>
            <span className="flex items-center">🔒 Secure Payments</span>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-green-700 mb-4">Ready to Start Your Journey?</h3>
          <p className="text-gray-600 mb-6">
            Join hundreds of families enjoying fresh, homemade food daily. Start your subscription today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.getElementById('plans-section').scrollIntoView({ behavior: 'smooth' })}
              className="bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-200 shadow-lg"
            >
              View Plans
            </button>
            <button 
              onClick={() => {
                if (!user) {
                  alert("Please log in to view your plan");
                  navigate("/login");
                  return;
                }
                navigate("/myplan");
              }}
              className="bg-white text-green-600 px-8 py-3 rounded-full font-semibold border-2 border-green-200 hover:bg-green-50 transition-all duration-200 shadow-lg"
            >
              My Plan
            </button>
          </div>
        </div>
      </div>
      
      
    </div>
  );
};

export default Home; 

